'use client'

import { useEffect, useState } from 'react'
import type { MenuItemData } from '@lib/types'
import throttle from 'lodash/fp/throttle'
import type { TypePlacePublic } from '@lib/location-types'
import { isInternalHref } from '@lib/mappers/menu-item-mapper'
import { Button, NavBar, NavItem } from '@shc/ui'
import useAnalytics from '@hooks/use-analytics'
import useScreenMediaQuery from '@hooks/useScreenMediaQuery'
import NextLink from 'next/link'

type LocationNavbarProps = {
  menuItems: MenuItemData[]
  schedulingCtaUrl: TypePlacePublic['scheduling_cta_url']
  schedulingType: TypePlacePublic['scheduling_type']
  schedulingCtaLabel: TypePlacePublic['scheduling_cta_label']
}

const LocationNavbar = ({
  schedulingCtaUrl,
  schedulingType,
  schedulingCtaLabel,
  menuItems,
}: LocationNavbarProps) => {
  const { trackSnowplow } = useAnalytics()
  const throttleMs = 100
  const subNavBarHeight = 64
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [showButton, setShowButton] = useState(false)

  const sectionElementIds = menuItems.map((link) => {
    const href = link.route
    const sectionIndex = href.indexOf('#')
    if (sectionIndex != -1) {
      return href.substring(sectionIndex + 1)
    }
    return null
  })

  useEffect(() => {
    setActiveSection(typeof window !== 'undefined' ? window.location.hash.slice(1) : null)
  }, [])

  useEffect(() => {
    const handleActiveSection = () => {
      for (const sectionElementId of sectionElementIds) {
        if (sectionElementId) {
          const section = document.getElementById(sectionElementId)
          const scrollOffSet = window.scrollOffSet

          if (section && section.getBoundingClientRect().top < scrollOffSet + subNavBarHeight) {
            setActiveSection(sectionElementId)
          }
        }
      }
    }

    const handleButtonVisibility = () => {
      const firstSectionId = sectionElementIds[1]
      if (firstSectionId) {
        const firstSection = document.getElementById(firstSectionId)
        const scrollOffSet = window.scrollOffSet

        if (firstSection && firstSection.getBoundingClientRect().top <= scrollOffSet) {
          setShowButton(true)
        } else {
          setShowButton(false)
        }
      }
    }

    const handleScroll = throttle(throttleMs, () => {
      handleActiveSection()
      handleButtonVisibility()
    })

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <NavBar
      id="sub-nav"
      alignment="start"
      sticky
      rightElement={
        schedulingCtaUrl &&
        showButton && (
          <NavbarButton
            schedulingCtaUrl={schedulingCtaUrl}
            schedulingCtaLabel={schedulingCtaLabel}
            schedulingType={schedulingType}
          />
        )
      }>
      {menuItems.map(({ name, route, isInternal }) => {
        const active = activeSection === route.split('#')[1]
        const handleClick = () => {
          trackSnowplow({
            event: {
              name: 'navigation_click',
              data: {
                navigation_tree: name,
                navigation_level: 1,
                navigation_subject: name,
                navigation_url: route,
              },
            },
            contexts: [{ name: 'section', data: { section_name: 'subnav' } }],
          })
        }
        return (
          <NavItem
            as={NextLink}
            key={name}
            href={route}
            target={isInternal ? undefined : '_blank'}
            rel={isInternal ? undefined : 'noopener noreferrer'}
            active={active}
            onClick={handleClick}>
            {name}
          </NavItem>
        )
      })}
    </NavBar>
  )
}

const NavbarButton = ({
  schedulingCtaUrl,
  schedulingCtaLabel,
  schedulingType,
}: Omit<LocationNavbarProps, 'menuItems'>) => {
  const isSchedulingCtaInternal =
    isInternalHref(schedulingCtaUrl ?? '') || schedulingType === 'Epic Open Scheduling'
  const isLargerThenMobile = useScreenMediaQuery('lg')
  const { trackSnowplow } = useAnalytics()

  const button = (
    <Button
      size="sm"
      width="auto"
      id="scheduling-button-navbar"
      as="a"
      href={schedulingCtaUrl ?? ''}
      target={isSchedulingCtaInternal ? undefined : '_blank'}
      rel={isSchedulingCtaInternal ? undefined : 'noopener noreferrer'}
      onClick={() => {
        trackSnowplow({
          event: {
            name: 'navigation_click',
            data: {
              navigation_tree: schedulingCtaLabel ?? 'undefined',
              navigation_level: 1,
              navigation_subject: schedulingCtaLabel ?? 'undefined',
              navigation_url: schedulingCtaUrl,
            },
          },
          contexts: [{ name: 'section', data: { section_name: 'subnav' } }],
        })
      }}>
      {schedulingCtaLabel}
    </Button>
  )
  if (!isLargerThenMobile) {
    return (
      <div className="w-full bg-white fixed bottom-0 inset-x-0 flex justify-center items-center py-3 border-t-[1px] border-gray-100">
        {button}
      </div>
    )
  }

  return button
}

export default LocationNavbar
